import { useEffect, useRef } from "react";
import type { RenderScanabilityReport, TemplateSummary } from "../api";

export type TileRenderState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; svg: string; payload: string; warnings: { code: string; message: string }[]; scanability: RenderScanabilityReport | null }
  | { status: "error" };

type Props = {
  template: TemplateSummary;
  state: TileRenderState;
  onVisible: (templateId: string, version: number) => void;
  onSelect: (
    template: TemplateSummary,
    svg: string,
    payload: string,
    warnings: { code: string; message: string }[],
    scanability: RenderScanabilityReport | null,
  ) => void;
};

/** Một ô mẫu trong lưới chọn mẫu — chỉ gọi render khi lọt vào tầm nhìn (IntersectionObserver). */
export default function TemplateTile({ template, state, onVisible, onSelect }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const observedRef = useRef(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || observedRef.current) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !observedRef.current) {
            observedRef.current = true;
            onVisible(template.templateId, template.version);
            io.disconnect();
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.templateId, template.version]);

  const selectable = state.status === "done";

  return (
    <div className="template-tile" ref={rootRef}>
      <div
        className="template-tile-preview"
        role={selectable ? "button" : undefined}
        tabIndex={selectable ? 0 : -1}
        aria-label={selectable ? `Chọn mẫu ${template.name}` : undefined}
        onClick={() => {
          if (state.status === "done") onSelect(template, state.svg, state.payload, state.warnings, state.scanability);
        }}
        onKeyDown={(e) => {
          if (state.status === "done" && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onSelect(template, state.svg, state.payload, state.warnings, state.scanability);
          }
        }}
      >
        {state.status === "done" ? (
          <div className="template-tile-svg" dangerouslySetInnerHTML={{ __html: state.svg }} />
        ) : state.status === "error" ? (
          <div className="template-tile-status template-tile-error">
            <span>Chưa tạo được ảnh mẫu này</span>
          </div>
        ) : (
          <div className="template-tile-status template-tile-skeleton">
            <span className="spinner spinner-sm" aria-hidden="true" />
            <span>Đang chuẩn bị mã QR của bạn…</span>
          </div>
        )}
      </div>
      <span className="template-tile-name">{template.name}</span>
    </div>
  );
}
